'use client';

import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import LoginDialog from '@/app/dashboard/property-manager/components/LoginDialog';
import ThemeToggle from '@/components/ThemeToggle';

const NAV_LINKS = [
  { label: 'Features',  href: '/landing#features'  },
  { label: 'Demos',     href: '/landing#demos'      },
  { label: 'Pricing',   href: '/landing#pricing'    },
  { label: 'Portfolio', href: '/landing#portfolio'  },
  { label: 'Docs',      href: '/landing#docs'       },
];

export default function Header() {
  const [scrolled,        setScrolled]        = useState(false);
  const [loginOpen,       setLoginOpen]       = useState(false);
  const [mobileMenuOpen,  setMobileMenuOpen]  = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
      if (window.scrollY > 60) setMobileMenuOpen(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <>
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || mobileMenuOpen
            ? 'bg-[#09090B]/95 backdrop-blur-xl border-b border-[rgba(124,58,237,0.15)] shadow-[0_4px_30px_rgba(0,0,0,0.4)]'
            : 'bg-transparent'
        }`}
      >
        {/* ── Main bar ──────────────────────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-2.5 min-w-0">
            <AppLogo
              src="/assets/images/app_.logo.png"
              size={36}
              text="Propmatic"
              className="text-[#EEEEF0] font-manrope font-bold text-xl tracking-tight"
            />
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7">
            {NAV_LINKS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-sm font-medium text-[#9999A8] hover:text-[#EEEEF0] transition-colors duration-200 font-manrope"
              >
                {item.label}
              </a>
            ))}
            <a
              href="/ai-features"
              className="group inline-flex items-center gap-1.5 text-sm font-semibold font-manrope px-3 py-1.5 rounded-full border border-[rgba(124,58,237,0.35)] bg-[rgba(124,58,237,0.08)] text-[#A78BFA] hover:bg-[rgba(124,58,237,0.18)] hover:border-[rgba(124,58,237,0.6)] transition-all duration-200"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] status-pulse" />
              AI Features
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="opacity-60">
                <path d="M7 17L17 7M10 7h7v7" />
              </svg>
            </a>
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />

            {/* Sign in — hidden on xs */}
            <button
              onClick={() => setLoginOpen(true)}
              className="hidden sm:inline-flex items-center text-sm text-[#9999A8] hover:text-[#EEEEF0] transition-colors font-manrope font-medium"
            >
              Sign in
            </button>

            {/* CTA — hidden on mobile, shown md+ */}
            <a
              href="#demo"
              className="cta-primary hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm tracking-tight whitespace-nowrap"
            >
              See Your Portfolio Live
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-xl border border-[rgba(255,255,255,0.07)] bg-[rgba(255,255,255,0.04)] text-[#9999A8] hover:text-[#EEEEF0] hover:border-[rgba(255,255,255,0.15)] transition-all"
            >
              {mobileMenuOpen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* ── Mobile menu drawer ─────────────────────────────────────────── */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            mobileMenuOpen ? 'max-h-[480px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav className="px-4 pb-5 pt-2 flex flex-col gap-1 border-t border-[rgba(255,255,255,0.05)]">
            {NAV_LINKS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={closeMobile}
                className="flex items-center px-3 py-3 rounded-xl text-sm font-medium text-[#9999A8] hover:text-[#EEEEF0] hover:bg-[rgba(255,255,255,0.04)] transition-colors font-manrope"
              >
                {item.label}
              </a>
            ))}
            <a
              href="/ai-features"
              onClick={closeMobile}
              className="flex items-center gap-2 px-3 py-3 rounded-xl text-sm font-semibold text-[#A78BFA] hover:bg-[rgba(124,58,237,0.08)] transition-colors font-manrope"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#A78BFA] status-pulse" />
              AI Features
            </a>

            <div className="h-px bg-[rgba(255,255,255,0.06)] my-2" />

            {/* Sign in */}
            <button
              onClick={() => { setLoginOpen(true); closeMobile(); }}
              className="flex items-center px-3 py-3 rounded-xl text-sm font-medium text-[#9999A8] hover:text-[#EEEEF0] hover:bg-[rgba(255,255,255,0.04)] transition-colors font-manrope text-left"
            >
              Sign in
            </button>

            {/* Mobile CTA */}
            <a
              href="#demo"
              onClick={closeMobile}
              className="cta-primary flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold tracking-tight mt-1"
            >
              See Your Portfolio Live
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </nav>
        </div>
      </header>
    </>
  );
}